import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetPasswordPage from './pages/SetPasswordPage';
import DashboardLayout from './pages/DashboardLayout';
import CompaniesPage from './pages/CompaniesPage';
import GenerateOfferPage from './pages/GenerateOfferPage';
import HistoryPage from './pages/HistoryPage';
import UsersPage from './pages/UsersPage';

// Logged in, and not stuck on a temporary password
function ProtectedRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/login" replace />;
  if (admin.mustChangePassword) return <Navigate to="/set-password" replace />;
  return children;
}

// Admin-only pages. A USER who types the URL is sent to Generate Offer.
// (The backend also rejects their API calls, so this is just for a clean UI.)
function AdminRoute({ children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/generate" replace />;
  return children;
}

// Only reachable while the user must change their temporary password
function SetPasswordRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/login" replace />;
  if (!admin.mustChangePassword) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { admin } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={admin ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/forgot-password" element={admin ? <Navigate to="/" replace /> : <ForgotPassword />} />
      <Route path="/reset-password/:token" element={admin ? <Navigate to="/" replace /> : <ResetPassword />} />
      <Route
        path="/set-password"
        element={
          <SetPasswordRoute>
            <SetPasswordPage />
          </SetPasswordRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/generate" replace />} />
        <Route path="generate" element={<GenerateOfferPage />} />
        <Route path="history" element={<HistoryPage />} />
        <Route path="companies" element={<AdminRoute><CompaniesPage /></AdminRoute>} />
        <Route path="users" element={<AdminRoute><UsersPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
